import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
  CollectionReference,
  Query,
} from "@angular/fire/firestore";
import { environment } from "../../environments/environment";
import { User } from "../@core/data/users";
import { UserService } from "../@core/services/user.service";
import CourseSection from "../models/course-content.model";
import Course from "../models/course.model";
import Enrollment, {
  EnrollmentLecture,
  EnrollmentSection,
} from "../models/enrollment.model";
import * as firebase from "firebase/app";
import "firebase/firestore";
import { AngularFireFunctions } from "@angular/fire/functions";
import { NotificationService } from "./notifications.service";

@Injectable({
  providedIn: "root",
})
export class StudentService {
  visibility = environment.visibility;
  baseUrl = environment.apiUrl;
  userLogin: User;
  private dbPath = "/enrollments";
  enrollRef: AngularFirestoreCollection<Enrollment>;
  sectionsRef: AngularFirestoreCollection<EnrollmentSection>;
  lecturesRef: AngularFirestoreCollection<EnrollmentLecture>;

  constructor(
    private http: HttpClient,
    private fns: AngularFireFunctions,
    private db: AngularFirestore,
    private userStore: UserService,
    private notiService: NotificationService
  ) {
    this.enrollRef = db.collection(this.dbPath);

    this.userStore.getCurrentUser().subscribe((user: User) => {
      this.userLogin = user;
    });
  }

  getAll(): AngularFirestoreCollection<Enrollment> {
    return this.enrollRef;
  }

  getEnrollsByCourse(
    tutorId?: string,
    courseId?: string,
    orderBy?: string,
    limit?: number,
    cursor?: any
  ): AngularFirestoreCollection<Enrollment> {
    return this.db.collection(this.dbPath, (ref) => {
      let query: CollectionReference | Query = ref;

      query = query.where("isDeleted", "==", false);
      query = query.where("isActive", "==", true);
      query = query.where("courseId", "==", courseId);
      query = query.where("tutorId", "==", tutorId);

      if (orderBy && orderBy != "") {
        query = query.orderBy(orderBy, "desc");
      } else {
        query = query.orderBy("updatedOn", "desc");
      }

      query = limit ? query.limit(limit) : query.limit(50);
      if (cursor != null) query = query.startAfter(cursor);

      return query;
    });
  }

  getDashboardEnrolls(userId?: string): AngularFirestoreCollection<Enrollment> {
    const createdBy = this.userLogin ? this.userLogin.uid : userId;

    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("createdBy", "==", createdBy)
        .orderBy("updatedOn", "desc")
        .limit(6)
    );
  }

  getFinishedCourses(userId, finished): AngularFirestoreCollection<Enrollment> {
    const createdBy = this.userLogin ? this.userLogin.uid : userId;
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("finished", "==", finished)
        .where("createdBy", "==", createdBy)
    );
  }

  getCoursesByStudent(userId?: string): AngularFirestoreCollection<Enrollment> {
    const createdBy = this.userLogin ? this.userLogin.uid : userId;

    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("createdBy", "==", createdBy)
        .orderBy("updatedOn", "desc")
    );
  }

  checkExistEnrolCourse(
    courseId?: string,
    uid?: string
  ): AngularFirestoreCollection<Enrollment> {
    const createdBy = uid ? uid : this.userLogin ? this.userLogin.uid : "";

    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("createdBy", "==", createdBy)
        .where("courseId", "==", courseId)
        .orderBy("createdOn", "desc")
    );
  }

  // get(id: string) {
  //   return this.enrollRef.doc(id).valueChanges(["added", "removed"]);
  // }

  getEnrolmentById(id: string) {
    var db = firebase.default.firestore();
    var ref = db.collection(this.dbPath).doc(id);

    return ref.get();
  }

  create(course: Course): any {
    var enrol = new Enrollment();
    enrol.courseId = course.id;
    enrol.tutorId = course.createdBy;
    enrol.syncOn = course.updatedOn;
    enrol.course = course;

    enrol.finished = false;
    enrol.noSectionFinished = 0;
    enrol.noLectureFinished = 0;

    enrol.status = 1; //1: ready, 0: pending, -1: denied
    enrol.isActive = true;
    enrol.isDeleted = false;
    enrol.createdOn = new Date();
    enrol.createdBy = this.userLogin.uid;
    enrol.updatedOn = new Date();
    enrol.updatedBy = this.userLogin.uid;
    enrol.author = this.userLogin;
    enrol.visibility = this.visibility;

    return this.enrollRef.add({ ...enrol }).then((docRef) => {
      enrol.id = docRef.id;

      //Send notification to tutor
      this.notiService.create(
        "Đăng ký",
        `/enrollments`,
        enrol.id,
        enrol.courseId,
        `${enrol.course.title}`,
        ``,
        enrol.tutorId,
        `/tutor/courses/${enrol.courseId}`,
        enrol.courseId
      );

      return docRef.id;
    });
  }

  updateCourseInfo(id: string, course: Course): Promise<void> {
    const dateNow = new Date();

    return this.enrollRef.doc(id).update({
      course: course,
      syncOn: dateNow,
      author: this.userLogin,
      updatedOn: dateNow,
    });
  }

  updateEnrolSummary(
    id: string,
    noSectionFinished: number,
    noLectureFinished: number
  ): Promise<void> {
    const dateNow = new Date();

    return this.enrollRef.doc(id).update({
      noSectionFinished: noSectionFinished,
      noLectureFinished: noLectureFinished,
      updatedOn: dateNow,
    });
  }

  isActive(id: string, value: boolean): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    return this.enrollRef
      .doc(id)
      .update({ isActive: value, updatedOn: updatedOn, updatedBy: updatedBy });
  }

  receivedGift(id: string) {
    const callable = this.fns.httpsCallable("student-receivedGift");
    return callable({ id: id });
  }

  inviteStudent(courseId: string, uidStudent: string) {
    const callable = this.fns.httpsCallable("student-invite");
    return callable({ courseId: courseId, uidStudent: uidStudent });
  }

  responseInvite(enrol: Enrollment, status: number): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    let description = status === 1 ? "Đồng ý tham gia" : "Từ chối lời mời";

    const active = status === 1 ? true : false;
    return this.enrollRef
      .doc(enrol.id)
      .update({
        status: status,
        isActive: active,
        updatedOn: updatedOn,
        updatedBy: updatedBy,
        author: this.userLogin,
      })
      .then((docRef) => {
        //Send notification to tutor
        this.notiService.create(
          "",
          `/enrollments`,
          enrol.id,
          enrol.courseId,
          description,
          `${enrol.course.title}`,
          enrol.tutorId,
          `/tutor/courses/${enrol.courseId}`,
          enrol.courseId
        );

        return docRef;
      });
  }

  delete(id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    return this.enrollRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  syncEnrollment(enrol) {
    enrol.createdOn ? delete enrol.createdOn : null;
    enrol.updatedOn ? delete enrol.updatedOn : null;
    enrol.finishedOn ? delete enrol.finishedOn : null;
    enrol.fsId = enrol.id ? enrol.id : "";

    if (environment.syncLive) {
      this.http.post(this.baseUrl + "/Enrollments", enrol).subscribe({
        next: () => {},
        error: (err) => {},
      });
    }
  }
}
